import { apiListAll } from '@/core/api/resources'
import { currentVariant } from '@/core/data/session'
import type { Modal, CoachingModal } from '@/core/state/appStore'

export type AufgabeLanding = { modal: CoachingModal; tab: string }

export type AufgabeEntry = {

  dbName: string

  position: number
  landing: AufgabeLanding | null
}

export const AUFGABE = {

  stammdaten: {
    dbName: 'Stammdaten abwarten',
    position: 1,
    landing: { modal: 'avgs', tab: 'stammdaten' },
  },

  avgsUpload: {
    dbName: 'AVGS hochladen',
    position: 2,
    landing: { modal: 'avgs', tab: 'upload' },
  },

  avgsPruefen: {
    dbName: 'AVGS prüfen',
    position: 3,
    landing: { modal: 'avgs', tab: 'avgs' },
  },

  tbHochladen: {
    dbName: 'Trägerbestätigung hochladen',
    position: 4,
    landing: { modal: 'avgs', tab: 'tb-hochladen' },
  },

  tbAusfuellen: {
    dbName: 'Trägerbestätigung ausfüllen',
    position: 5,
    landing: { modal: 'avgs', tab: 'bestaetigung' },
  },

  tbPruefen: {
    dbName: 'Trägerbestätigung prüfen',
    position: 6,
    landing: { modal: 'avgs', tab: 'bestaetigung' },
  },

  vertragErstellen: {
    dbName: 'Vertrag erstellen',
    position: 7,
    landing: { modal: 'vertrag', tab: 'vertrag' },
  },

  vertragUnterschreiben: {
    dbName: 'Vertragsunterschrift abwarten',
    position: 8,
    landing: { modal: 'vertrag', tab: 'vertrag' },
  },

  bewilligung: {
    dbName: 'Bewilligung hochladen',
    position: 9,
    landing: { modal: 'bewilligung', tab: 'hochladen' },
  },

  bewilligungPruefen: {
    dbName: 'Bewilligung prüfen',
    position: 10,
    landing: { modal: 'bewilligung', tab: 'bewilligung' },
  },

  ecnFuehren: {
    dbName: 'Nachweis ausfüllen',
    position: 11,
    landing: { modal: 'ecn', tab: 'nachweis' },
  },
  nachweiseGegenzeichnen: {
    dbName: 'Nachweisunterschrift abwarten',
    position: 12,
    landing: { modal: 'ecn', tab: 'nachweis' },
  },

  abschlussbericht: {
    dbName: 'Abschlussbericht ausfüllen',
    position: 13,
    landing: { modal: 'abschluss', tab: 'bericht' },
  },

  abschlussberichtPruefen: {
    dbName: 'Abschlussbericht prüfen',
    position: 14,
    landing: { modal: 'abschluss', tab: 'pruefen' },
  },

  zertifikatPruefen: {
    dbName: 'Zertifikat prüfen',
    position: 16,
    landing: { modal: 'abschluss', tab: 'zertifikat' },
  },

  teilnehmendenlistePruefen: {
    dbName: 'Teilnehmendenliste prüfen',
    position: 15,
    landing: { modal: 'abschluss', tab: 'teilnehmendenliste' },
  },

  teilnehmendenlisteUnterschrift: {
    dbName: 'Signatur von Teilnehmendenliste abwarten',
    position: 17,
    landing: { modal: 'abschluss', tab: 'zertifikat' },
  },
  abgeschlossen: { dbName: 'Abgeschlossen', position: 18, landing: null },
} satisfies Record<string, AufgabeEntry>

export type AufgabeKey = keyof typeof AUFGABE

const AUFGABE_BY_NAME = new Map<string, AufgabeEntry>(
  Object.values(AUFGABE).map((e) => [e.dbName, e]),
)

function isAzavContext(): boolean {
  try {
    return currentVariant() === 'azav'
  } catch {
    return true
  }
}

export function aufgabeForName(name: string | null | undefined): AufgabeEntry | undefined {
  if (!name || !isAzavContext()) return undefined
  return AUFGABE_BY_NAME.get(name)
}

export function positionForAufgabeName(name: string | null | undefined): number | null {
  return aufgabeForName(name)?.position ?? null
}

export function positionsForModal(modal: Modal): number[] {
  return Object.values(AUFGABE)
    .filter((e) => e.landing?.modal === modal)
    .map((e) => e.position)
    .sort((a, b) => a - b)
}

export function phaseTabBounds(modal: Modal): { first: string; last: string } | null {
  const entries = Object.values(AUFGABE)
    .filter((e) => e.landing?.modal === modal)
    .sort((a, b) => a.position - b.position)
  if (entries.length === 0) return null
  return { first: entries[0].landing!.tab, last: entries[entries.length - 1].landing!.tab }
}

export function aufgabenForTab(modal: Modal, tab: string): AufgabeEntry[] {
  return Object.values(AUFGABE)
    .filter((e) => e.landing?.modal === modal && e.landing?.tab === tab)
    .sort((a, b) => a.position - b.position)
}

export function tabLabelFor(
  modal: Modal,
  tab: string,
  currentAufgabeName: string | null | undefined,
): string | null {
  const span = aufgabenForTab(modal, tab)
  if (span.length === 0) return null
  if (span.length === 1) return span[0].dbName

  const current = isAzavContext() ? span.find((e) => e.dbName === currentAufgabeName) : undefined
  return (current ?? span[0]).dbName
}

const aufgabeIds = new Map<AufgabeKey, string>()

export async function aufgabeIdFor(key: AufgabeKey): Promise<string> {
  const hit = aufgabeIds.get(key)
  if (hit) return hit
  const dbName = AUFGABE[key].dbName
  const rows = await apiListAll('/aufgabe')
  const row = rows.find((r) => r.name === dbName)
  if (!row) {
    throw new Error(
      `Aufgabe "${dbName}" ist in der Datenbank noch nicht angelegt (Migration evtl. noch nicht angewendet?).`,
    )
  }
  aufgabeIds.set(key, row.id)
  return row.id
}
